/**
 * SnykConnectionForm — "Add Snyk Connection".
 *
 * Registers a Snyk connection (organization + API key) so LeftShield can import vulnerabilities.
 * `POST` via `useCreateSnykConnection()`.
 *
 * Pattern: `react-hook-form` + `zodResolver`, schema imported from `SnykConnection.schema.ts`
 * (never redefined), Fluent `Field` for verbatim validation messages, server errors mapped via
 * the shared `applyServerErrors` (Charter M3 / form-standard.context.md). The outer `Dialog`
 * chrome is owned by `IntegrationsPage.tsx` (this feature's own `components/`, not `src/pages/**`)
 * — this component renders the form CONTENT only and keeps its existing `onSuccess`/`onCancel`
 * prop contract so the hosting page needs no change.
 */
import type { FC } from 'react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Field, Input, makeStyles, tokens } from '@fluentui/react-components';
import { ShieldCheckmarkRegular } from '@fluentui/react-icons';
import { useCreateSnykConnection } from '../hooks';
import type { CreateSnykConnectionRequest } from '../types';
import { applyServerErrors, useReportFormDirty, FormActions, ServerErrorBanner } from '../../../components/forms';
import {
  SnykConnectionSchema,
  type SnykConnectionFormValues,
  type SnykConnectionFormOutput,
} from './SnykConnection.schema';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

// Known request-DTO field keys — used by the server-error -> field mapping.
const KNOWN_FIELDS = ['name', 'organizationId', 'apiKey'] as const;

const DEFAULT_VALUES: SnykConnectionFormValues = {
  name: '',
  organizationId: '',
  apiKey: '',
};

export interface SnykConnectionFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  /** Reports dirty state up so the hosting dialog can guard an unsaved-changes dismiss. */
  onDirtyChange?: (dirty: boolean) => void;
}

export const SnykConnectionForm: FC<SnykConnectionFormProps> = ({ onSuccess, onCancel, onDirtyChange }) => {
  const styles = useStyles();

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isDirty },
  } = useForm<SnykConnectionFormValues, unknown, SnykConnectionFormOutput>({
    resolver: zodResolver(SnykConnectionSchema),
    mode: 'onTouched',
    defaultValues: DEFAULT_VALUES,
  });

  useReportFormDirty(isDirty, onDirtyChange);

  const [globalError, setGlobalError] = useState<string | undefined>();

  const createConnection = useCreateSnykConnection();

  const onValid = (values: SnykConnectionFormOutput) => {
    // Guard double-submit.
    if (createConnection.isPending) return;
    setGlobalError(undefined);

    const request: CreateSnykConnectionRequest = {
      name: values.name,
      organizationId: values.organizationId,
      apiKey: values.apiKey,
    };

    createConnection.mutate(request, {
      onSuccess: () => {
        reset(DEFAULT_VALUES);
        onSuccess?.();
      },
      onError: (error) => {
        const { globalMessage } = applyServerErrors(error, KNOWN_FIELDS, setError);
        setGlobalError(globalMessage);
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onValid)} className={styles.form} noValidate>
      <ServerErrorBanner title="Error" message={globalError} testId="snyk-connection-form-server-error" />

      <Field
        label="Connection Name"
        required
        validationState={errors.name ? 'error' : undefined}
        validationMessage={errors.name?.message}
      >
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="snyk-connection-form-name"
              value={field.value ?? ''}
              placeholder="My Snyk Organization"
              disabled={createConnection.isPending}
            />
          )}
        />
      </Field>

      <Field
        label="Organization ID"
        required
        validationState={errors.organizationId ? 'error' : undefined}
        validationMessage={errors.organizationId?.message}
        hint="Found in Snyk Organization Settings (UUID format)"
      >
        <Controller
          name="organizationId"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="snyk-connection-form-organizationId"
              value={field.value ?? ''}
              placeholder="12345678-abcd-1234-abcd-1234567890ab"
              disabled={createConnection.isPending}
            />
          )}
        />
      </Field>

      <Field
        label="API Key"
        required
        validationState={errors.apiKey ? 'error' : undefined}
        validationMessage={errors.apiKey?.message}
        hint="From https://app.snyk.io/account — requires Enterprise plan"
      >
        <Controller
          name="apiKey"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type="password"
              data-testid="snyk-connection-form-apiKey"
              value={field.value ?? ''}
              placeholder="Your Snyk API key"
              disabled={createConnection.isPending}
            />
          )}
        />
      </Field>

      <FormActions
        mode="create"
        testIdPrefix="snyk-connection-form"
        isSubmitting={createConnection.isPending}
        onCancel={onCancel}
        submitLabel="Create Connection"
        submittingLabel="Creating…"
        submitIcon={<ShieldCheckmarkRegular />}
      />
    </form>
  );
};
